import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {UserAPIController} from '../../../../../../../../api/user-api/user-api-controller';
import {UserAPIRequestImpl} from '../../../../../../../../api/user-api/user-api-request';
import {BaseURL} from '../../../../../../../../configs/request';
import {useApiCalls} from '../../../../../../../../hooks/init-calls';
import {useStoreContext} from '../../../../../../../../store/store-context';
import {authStore} from '../../../../../../../../store/stores/auth-store';
import {Avatar} from '../../../../../../../../ui/atoms/avatar';
import {HorizontalDots} from '../../../../../../../../ui/atoms/icons/horizontal-dots';
import {MagnifyingGlassIcon} from '../../../../../../../../ui/atoms/icons/magnifying-glass';
import {Tooltip} from '../../../../../../../../ui/molecules/tooltip';
import {activateNotification} from '../../../../../../../../ui/molecules/notification/activate-notification';
import {ItemLoaderLine} from '../../../../../../../../ui/organisms/item-loader-line';
import {setFirebaseData} from '../../../../../../../../utils/firebase-handler';
import {generateRandomDigits} from '../../../../../../../../utils/generate-random-numbers';
import {
  ConfirmCopy,
  DangerButton,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  GroupMore,
  GroupPill,
  InvitedTableHeader,
  InvitedTableRow,
  ManagerCell,
  MemberAction,
  MemberActionButton,
  MemberCard,
  MemberCell,
  MemberEmpty,
  MemberGroupsCell,
  MemberMenu,
  MemberMenuButton,
  MemberMenuRule,
  MemberPageButton,
  MemberPagination,
  MemberSearchBar,
  MemberSearchIcon,
  MemberSearchInput,
  SecondaryButton,
} from '../../member-redesign.styles';
import {reviewerId} from '../../member-role';
import {removeMemberFromPaginatedCache} from '../member-list-cache';

const memberName = (member: any) =>
  member?.firstName && member?.lastName
    ? `${member.firstName} ${member.lastName}`
    : member?.email || 'Unnamed member';

export const Invite = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [menuId, setMenuId] = useState('');
  const [revokingMember, setRevokingMember] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const {
    groupStore: {groups},
    usersStore: {users: workspaceUsers},
  } = useStoreContext();
  const {initGroupMembers} = useApiCalls();

  const invitedMembersQuery = useQuery(['inviteMembers', page], async () => {
    const controller = new UserAPIController(new UserAPIRequestImpl());
    return controller.fetchPaginatedInviteUsers(page);
  });
  const groupMembersQuery = useQuery(['initGroupMembers'], initGroupMembers);
  const response = invitedMembersQuery.data || {};
  const pageUsers = useMemo(
    () => (Array.isArray(response?.results) ? response.results : []),
    [response?.results],
  );

  const invitedUsers = useMemo(() => {
    const membership = groupMembersQuery.data;
    return pageUsers.map((user: any) => {
      const memberGroups = membership?.get?.(user.id) || [];
      const groupIds = memberGroups.map((item: any) => item.group);
      return {
        ...user,
        group: groupIds.length
          ? groups.filter((group: any) => groupIds.includes(group.id))
          : Array.isArray(user.group)
          ? user.group
          : [],
      };
    });
  }, [groupMembersQuery.data, groups, pageUsers]);

  const visibleUsers = invitedUsers.filter((user: any) =>
    [user.email, memberName(user)]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(search.toLowerCase())),
  );
  const totalPages = Number(response?.totalPages || 1);

  useEffect(() => {
    const closeMenu = () => setMenuId('');
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const resendInvite = async (email: string) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${BaseURL}/auth/resend-user-invitation`, {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
        },
        body: JSON.stringify({email}),
      });

      activateNotification({
        title: response.ok ? 'Invite sent' : 'Error',
        content: response.ok
          ? `A new invitation was sent to ${email}`
          : response.statusText || 'The invitation could not be resent.',
        kind: response.ok ? 'success' : 'error',
      });
    } catch {
      activateNotification({
        title: 'Error',
        content: 'The invitation could not be resent. Please try again.',
        kind: 'error',
      });
    }
    setActionLoading(false);
  };

  const revokeInvitation = useCallback(async () => {
    if (!revokingMember) return;
    setActionLoading(true);
    const controller = new UserAPIController(new UserAPIRequestImpl());
    const success = Boolean(await controller.deleteUser(revokingMember.id));

    if (success) {
      await queryClient.cancelQueries(['inviteMembers', page]);
      removeMemberFromPaginatedCache(
        queryClient,
        ['inviteMembers', page],
        revokingMember.id,
      );
      queryClient.invalidateQueries(['inviteMembers']);
      queryClient.invalidateQueries(['workspace-member-counts']);
      setFirebaseData('updates/user', generateRandomDigits(16));
      activateNotification({
        title: 'Invitation revoked',
        content: `${revokingMember.email} was removed from the invitation list.`,
        kind: 'success',
      });
      setRevokingMember(null);
    }
    setActionLoading(false);
  }, [page, queryClient, revokingMember]);

  return (
    <>
      <MemberCard>
        <MemberSearchBar>
          <MemberSearchInput
            aria-label="Search invited members"
            placeholder="Search members"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <MemberSearchIcon aria-hidden="true">
            <MagnifyingGlassIcon />
          </MemberSearchIcon>
        </MemberSearchBar>

        <InvitedTableHeader>
          <div>Email address</div>
          <div>Group(s)</div>
          <div>Manager</div>
          <div>Action</div>
        </InvitedTableHeader>

        {invitedMembersQuery.isLoading || groupMembersQuery.isLoading ? (
          <div style={{padding: 24}}>
            <ItemLoaderLine />
          </div>
        ) : visibleUsers.length === 0 ? (
          <MemberEmpty>
            {search
              ? 'No invited members match your search.'
              : 'There are no invited members.'}
          </MemberEmpty>
        ) : (
          visibleUsers.map((user: any) => {
            const manager =
              typeof user.reviewer === 'object'
                ? user.reviewer
                : (workspaceUsers || []).find(
                    (candidate: any) =>
                      candidate.id === reviewerId(user.reviewer),
                  );

            return (
              <InvitedTableRow key={user.id}>
                <MemberCell title={user.email}>{user.email}</MemberCell>
                <MemberGroupsCell>
                  {user.group?.length ? (
                    <>
                      <GroupPill title={user.group[0].name}>
                        {user.group[0].name}
                      </GroupPill>
                      {user.group.length > 1 && (
                        <Tooltip
                          BodyTextNodeType={GroupMore}
                          text={`+${user.group.length - 1}`}
                          noCapitalize
                          bodyStyle={{cursor: 'help'}}
                          tooltipBody={user.group
                            .slice(1)
                            .map((group: any) => group.name)
                            .join(', ')}
                        />
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </MemberGroupsCell>
                <ManagerCell>
                  {manager ? (
                    <>
                      <Avatar
                        name={memberName(manager)}
                        userId={manager.id || reviewerId(user.reviewer)}
                        src={manager.avatar?.url || ''}
                        size="sm"
                        tooltip={false}
                      />
                      <span title={memberName(manager)}>
                        {memberName(manager)}
                      </span>
                    </>
                  ) : (
                    '—'
                  )}
                </ManagerCell>
                <MemberAction onClick={(event) => event.stopPropagation()}>
                  <MemberActionButton
                    type="button"
                    aria-label={`Actions for ${user.email}`}
                    aria-expanded={menuId === user.id}
                    onClick={() =>
                      setMenuId(menuId === user.id ? '' : user.id)
                    }>
                    <HorizontalDots />
                  </MemberActionButton>
                  {menuId === user.id && (
                    <MemberMenu>
                      <MemberMenuButton
                        type="button"
                        disabled={actionLoading}
                        onClick={() => {
                          setMenuId('');
                          resendInvite(user.email);
                        }}>
                        Resend invitation
                      </MemberMenuButton>
                      <MemberMenuRule />
                      <MemberMenuButton
                        type="button"
                        $danger
                        onClick={() => {
                          setMenuId('');
                          setRevokingMember(user);
                        }}>
                        Revoke invitation
                      </MemberMenuButton>
                    </MemberMenu>
                  )}
                </MemberAction>
              </InvitedTableRow>
            );
          })
        )}

        {totalPages > 1 && !search && (
          <MemberPagination>
            <MemberPageButton
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              ‹
            </MemberPageButton>
            {Array.from({length: totalPages}, (_, index) => index + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - page) <= 1,
              )
              .map((number) => (
                <MemberPageButton
                  type="button"
                  key={number}
                  $active={number === page}
                  onClick={() => setPage(number)}>
                  {number}
                </MemberPageButton>
              ))}
            <MemberPageButton
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}>
              ›
            </MemberPageButton>
          </MemberPagination>
        )}
      </MemberCard>

      {revokingMember && (
        <DialogOverlay>
          <Dialog
            role="dialog"
            aria-modal="true"
            aria-label="Revoke invitation">
            <DialogHeader>
              <DialogTitle>Revoke invitation</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ConfirmCopy>
                Revoke the invitation sent to {revokingMember.email}? They will
                no longer be able to join using that invitation.
              </ConfirmCopy>
            </DialogBody>
            <DialogFooter>
              <SecondaryButton
                type="button"
                disabled={actionLoading}
                onClick={() => setRevokingMember(null)}>
                Cancel
              </SecondaryButton>
              <DangerButton
                type="button"
                disabled={actionLoading}
                onClick={revokeInvitation}>
                {actionLoading ? 'Working…' : 'Revoke invitation'}
              </DangerButton>
            </DialogFooter>
          </Dialog>
        </DialogOverlay>
      )}
    </>
  );
};
